import { z } from 'zod';

export const SIGNATURE_ASSET_MAX_BYTES = 512 * 1024;
export const SIGNATURE_ASSET_MAX_PER_USER = 5;

export const SIGNATURE_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
] as const;

export const signatureAssetSchema = z.object({
  id: z.string().uuid(),
  url: z.string(),
  contentType: z.enum(SIGNATURE_IMAGE_TYPES),
  byteSize: z.number().int(),
});

/** Pulls a Gmail-hosted image into local storage. See the route for why. */
export const importSignatureAssetSchema = z.object({
  url: z.string().url().max(2000),
});

export type SignatureAsset = z.infer<typeof signatureAssetSchema>;
export type ImportSignatureAssetInput = z.infer<typeof importSignatureAssetSchema>;

// ---------------------------------------------------------------------------
// Importing a whole signature out of the planner's sent mail
// ---------------------------------------------------------------------------

/**
 * Why an import produced nothing.
 *
 * This list is the feature's diagnostic surface, and it is longer than a
 * boolean because of who runs it first. The detector decides by comparing the
 * ends of the planner's sent messages, and what that comparison has to work
 * with — how many messages are readable, how many threads they span, whether
 * anything repeats — is invisible from the outside. The first person to
 * exercise it on real mail is a non-technical tester, and everything she can
 * tell us afterwards has to be legible in this one word plus the count of
 * messages we looked at.
 *
 * It was longer still when the design rested on Gmail's `div.gmail_signature`
 * marker, and the one time that shipped it imported an entire sent email as a
 * signature (EVE-45). The reasons below replace the ones that described it.
 *
 * So the reasons are the shapes a bug report can take, not internal states:
 *
 * - `not-connected` — there is no mailbox on the account at all. The fix is to
 *   connect one.
 * - `no-read-scope` — a mailbox is connected, but the grant is send-only, so
 *   nothing was scanned. The fix is to reconnect and grant read access.
 *
 *   These two are kept apart because they ask for different things. Telling a
 *   planner who has never connected Gmail that "this connection does not have
 *   read access" sends her looking for a permission screen she has no way to
 *   reach; telling a planner who granted send-only to "connect Gmail" sends her
 *   to a button she has already pressed. Both are 409 and both are persistent
 *   — neither is fixed by trying again — so the reason is the only thing
 *   carrying the remedy.
 * - `no-messages` — the Sent folder came back empty. Points at the query or
 *   the connection, never at the extractor.
 * - `too-few-messages` — the Sent folder could not supply three readable
 *   messages across two different threads, which is what comparing requires.
 *   Points at the sample rather than at the method: a mailbox too new, or one
 *   whose recent mail is a single long thread. Sending a few more emails fixes
 *   it, which no other reason here can claim.
 * - `no-repeated-block` — enough was read and nothing repeated at the end of
 *   it. Kept distinct from the above because it impeaches the premise instead
 *   of the sample: looking at more mail is exactly what will not help, and the
 *   investigation is whether this planner has a signature at all.
 * - `empty-after-sanitize` — a block was found and our own sanitizer emptied
 *   it. That is ours to fix, and it must never be reported as "not found".
 *
 * Two reasons were retired with the marker they described. `no-signature-block`
 * meant "no `div.gmail_signature` anywhere" and `only-in-quote` meant "every
 * marker sat inside quoted history" — neither is a state the detector can
 * reach, because it does not look for markers to decide anything. See
 * `backend/src/lib/signature-detect.ts` for why that changed.
 */
export const signatureImportReasonSchema = z.enum([
  'not-connected',
  'no-read-scope',
  'no-messages',
  'too-few-messages',
  'no-repeated-block',
  'empty-after-sanitize',
]);

/**
 * Which message the signature came out of.
 *
 * Present so the planner can be told what she is looking at before she saves
 * it — "Imported from your email to Amy Chen, 2 September" — because a
 * signature is only recognisable as hers in the context of a message she
 * remembers sending. A wrong pick is otherwise indistinguishable from a right
 * one until it is already saved.
 */
export const signatureImportSourceSchema = z.object({
  gmailMessageId: z.string(),
  /** The `To` display name, falling back to the address. Null if unreadable. */
  recipient: z.string().nullable(),
  /** ISO 8601, from Gmail's `internalDate`. Null when the message carries none. */
  sentAt: z.string().nullable(),
});

export const signatureImportResultSchema = z.discriminatedUnion('found', [
  z.object({
    found: z.literal(true),
    /** Sanitized, with every image already copied into our own storage. */
    html: z.string(),
    /** Images copied in, and images we had to drop. See the import route. */
    saved: z.number().int(),
    skipped: z.number().int(),
    scanned: z.number().int(),
    source: signatureImportSourceSchema,
  }),
  z.object({
    found: z.literal(false),
    reason: signatureImportReasonSchema,
    /**
     * Messages whose body we actually read, which is not the number listed: a
     * body that fails to fetch is skipped and not counted. `scanned: 0`
     * alongside `no-signature-block` therefore says "we could not read
     * anything", which is a different bug from "we read ten and none matched"
     * and would be invisible without the count.
     */
    scanned: z.number().int(),
  }),
]);

export type SignatureImportReason = z.infer<typeof signatureImportReasonSchema>;
export type SignatureImportSource = z.infer<typeof signatureImportSourceSchema>;
export type SignatureImportResult = z.infer<typeof signatureImportResultSchema>;
